/**
 * useDispatchStats Hook
 *
 * React Query hook for fetching dispatcher dashboard statistics.
 * Used by DispatchStatsRow component.
 *
 * Features:
 * - 30-second auto-polling
 * - Loading/error states
 * - Manual refresh capability
 */

import { useQuery } from '@tanstack/react-query';
import { fetchDashboardStats, DashboardStats } from '../../../../../api/dispatch';

/** Query key for dashboard stats */
export const DISPATCH_STATS_QUERY_KEY = ['dispatch', 'stats'];

/** Polling interval (30 seconds) */
const REFETCH_INTERVAL = 30 * 1000;

/** Stale time (15 seconds) */
const STALE_TIME = 15 * 1000;

export interface UseDispatchStatsOptions {
  /** Override polling interval (ms) */
  refetchInterval?: number;
  /** Disable auto-polling */
  enablePolling?: boolean;
  /** Minutes threshold for "on duty" (default: 30) */
  recentMinutes?: number;
}

export interface UseDispatchStatsResult {
  /** Dashboard statistics data */
  stats: DashboardStats | null;
  /** Whether initial load is in progress */
  isLoading: boolean;
  /** Whether any fetch is in progress (including refetch) */
  isFetching: boolean;
  /** Whether an error occurred */
  isError: boolean;
  /** Error object if present */
  error: Error | null;
  /** Last successful update timestamp */
  dataUpdatedAt: number | undefined;
  /** Manually trigger a refresh */
  refetch: () => void;
}

/**
 * Hook to fetch dispatcher dashboard statistics
 *
 * @param options - Configuration options
 * @returns Dashboard stats with loading/error states
 */
export function useDispatchStats(
  options: UseDispatchStatsOptions = {}
): UseDispatchStatsResult {
  const {
    refetchInterval = REFETCH_INTERVAL,
    enablePolling = true,
    recentMinutes,
  } = options;

  const {
    data,
    isLoading,
    isFetching,
    isError,
    error,
    dataUpdatedAt,
    refetch,
  } = useQuery({
    queryKey: [...DISPATCH_STATS_QUERY_KEY, recentMinutes],
    queryFn: () => fetchDashboardStats(recentMinutes),
    refetchInterval: enablePolling ? refetchInterval : false,
    staleTime: STALE_TIME,
  });

  return {
    stats: data ?? null,
    isLoading,
    isFetching,
    isError,
    error: error instanceof Error ? error : null,
    dataUpdatedAt,
    refetch,
  };
}

export default useDispatchStats;
